import React, { useEffect } from 'react';
import { BoxProps } from '@mui/material';
import { Trans } from '@lingui/macro';
import { TransactionResponse } from '@ethersproject/providers';
import { useRootStore } from 'src/store/root';
import { useWeb3Context } from '../../../libs/hooks/useWeb3Context';
import { useModalContext } from '../../../hooks/useModal';
import { getErrorTextFromError, TxAction } from '../../../ui-config/errorMapping';
import { queryClient } from '../../../../pages/_app.page';
import { QueryKeys } from '../../../ui-config/queries';
import { useAppDataContext } from '../../../hooks/app-data-provider/useAppDataProvider';
import { TxPositionActionsWrapper } from '../TxPositionActionsWrapper';

export interface WithdrawPositionActionsProps extends BoxProps {
  marketId?: number;
  suppliedTokenId?: string;
  isWrongNetwork: boolean;
}

export const WithdrawPositionActions = React.memo(
  ({ marketId, suppliedTokenId, isWrongNetwork }: WithdrawPositionActionsProps) => {
    const { user } = useAppDataContext();
    const { setGasLimit, setMainTxState, mainTxState, setTxError } = useModalContext();
    const { sendTx } = useWeb3Context();

    const position = user.userERC1155ReservesData.find((item) => item.tokenId === suppliedTokenId);

    const [
      withdrawPosition,
      estimateGasLimit,
    ] = useRootStore((state) => [
      state.withdrawPosition,
      state.estimateGasLimit,
      state,
    ]);

    const action = async () => {
      if (!marketId || !suppliedTokenId || !position || mainTxState.success) return;
      try {
        setTxError(undefined);
        setMainTxState({ ...mainTxState, loading: true });

        const supplyTx = withdrawPosition({
          marketId,
          tokenId: suppliedTokenId,
          nTokenAddress: position.nTokenAddress,
          balance: position.balance,
        });
        const supplyTxData = await estimateGasLimit(supplyTx);

        const response: TransactionResponse = await sendTx(supplyTxData);
        await response.wait(1);

        setMainTxState({
          txHash: response.hash,
          loading: false,
          success: true,
        });

        queryClient.invalidateQueries({ queryKey: [QueryKeys.POSITIONS] });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.SUPPLIED_POSITIONS_DATA] });

      } catch (error) {
        const parsedError = getErrorTextFromError(error, TxAction.GAS_ESTIMATION, false);
        setTxError(parsedError);
        setMainTxState({
          txHash: undefined,
          loading: false,
        });
      }
    };

    // Update gas estimation
    useEffect(() => {
      if (!marketId || !suppliedTokenId || !position|| mainTxState.success) return;

      const calculateGasLimit = async () => {
        const supplyTx = withdrawPosition({
          marketId,
          tokenId: suppliedTokenId,
          nTokenAddress: position.nTokenAddress,
          balance: position.balance,
        });
        const supplyTxData = await estimateGasLimit(supplyTx);
        setGasLimit(supplyTxData.gasLimit?.toString() || '0');
      }
      calculateGasLimit();
      const timerId = setInterval(calculateGasLimit, 15000);
      return () => {
        clearInterval(timerId);
      }
    }, [position?.balance, mainTxState.success]);

    return (
      <TxPositionActionsWrapper
        mainTxState={mainTxState}
        isWrongNetwork={isWrongNetwork}
        requiresAmount
        preparingTransactions={false}
        actionText={<Trans>Withdraw Position</Trans>}
        actionInProgressText={<Trans>Withdrawing Position</Trans>}
        handleAction={() => {
          window.gtag('event', 'lending_withdraw_modal_confirm', {
            asset: 'Uniswap position',
          });
          return action();
        }}
      />
    );
  }
);
